<div class="translation-manager-dialog">
  <!-- Header Section — sits outside the scroll body so it's always visible -->
  <div class="dialog-header">
    <div class="header-left">
      <div class="language-icon">
        <img src="assets/images/icons/language-dialog-red-icon.svg" alt="Language">
      </div>
      <h2 class="dialog-title">{{ 'translation.manager.language' | translateLang }}</h2>
    </div>
    <button class="close-button" (click)="close()" type="button" aria-label="Close">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="12" fill="#96080B"/>
        <path d="M16 8L8 16M8 8L16 16" stroke="white" stroke-width="2" stroke-linecap="round"/>
      </svg>
    </button>
  </div>

  <!-- Single unified scroll container: upper sections scroll first, translations follow -->
  <div class="dialog-scroll-body" cdkVirtualScrollableElement>

  <!-- Statistics and Actions Section (Combined) -->
  <div class="stats-actions-section">
    <div class="stats-section">
      <div class="stat-item">
        <span class="stat-label">{{ 'translation.manager.total_fields' | translateLang }}:</span>
        <span class="stat-value total">{{ totalFields() }}</span>
      </div>
      <div class="stat-item">
        <span class="stat-label">{{ 'translation.manager.translated_fields' | translateLang }}:</span>
        <span class="stat-value translated">{{ translatedFields() }}</span>
      </div>
    </div>

    <div class="actions-section">
      <label class="action-button" [class.disabled]="!selectedLanguage() || isLoading()">
        {{ 'translation.manager.import' | translateLang }}
        <input
          type="file"
          accept=".csv"
          (change)="importTranslations($event)"
          [disabled]="!selectedLanguage() || isLoading()"
          hidden>
      </label>
      <button
        class="action-button"
        (click)="exportTranslations()"
        [disabled]="!selectedLanguage() || isLoading()"
        type="button">
        {{ 'translation.manager.export' | translateLang }}
      </button>
    </div>
  </div>

  <!-- Filter Controls and Save Actions (Combined) -->
  <div class="filters-save-section">
    <div class="filters-section">
      <div class="filter-item">
        <app-select
          [label]="'translation.manager.language' | translateLang"
          [options]="languageOptions()"
          [value]="selectedLanguage()?.code || ''"
          [placeholder]="'translation.manager.select_language' | translateLang"
          (valueChange)="onLanguageChange($event)">
        </app-select>
      </div>
      <div class="filter-item">
        <app-select
          [label]="'translation.manager.category' | translateLang"
          [options]="categoryOptions()"
          [value]="selectedCategory()"
          [placeholder]="'translation.manager.select_category' | translateLang"
          (valueChange)="onCategoryChange($event)">
        </app-select>
      </div>
      <div class="filter-item">
        <app-search-field
          [label]="'translation.manager.search' | translateLang"
          [placeholder]="'translation.manager.search_placeholder' | translateLang"
          [value]="searchTermModel"
          [dropdownItems]="[]"
          [showSearchIcon]="true"
          (search)="onSearchChange($event)">
        </app-search-field>
      </div>
    </div>

    <div class="save-actions-section">
      <app-button
        [label]="'translation.manager.save_all' | translateLang"
        variant="yellow"
        [disabled]="!hasDirtyTranslations() || isLoading()"
        (clicked)="saveAllChanges()">
      </app-button>
      @if (showPublishButton()) {
        <button
          class="btn-secondary-action"
          (click)="confirmPublish()"
          [disabled]="isLoading()"
          type="button">
          <p>{{ 'translation.manager.publish' | translateLang }}</p>
        </button>
      }
      @if (showUnpublishButton()) {
        <button
          class="btn-secondary-action"
          (click)="confirmUnpublish()"
          [disabled]="isLoading()"
          type="button">
          <p>{{ 'translation.manager.unpublish' | translateLang }}</p>
        </button>
      }
      <button
        class="btn-secondary-action"
        (click)="confirmDiscard()"
        [disabled]="!hasDirtyTranslations() || isLoading()"
        type="button">
        <p>{{ 'translation.manager.discard' | translateLang }}</p>
      </button>
    </div>
  </div>

  <!-- Editing Mode Banner -->
  @if (selectedLanguage() && hasDirtyTranslations()) {
    <div class="editing-banner">
      <div class="editing-banner__left">
        <div class="editing-banner__status">
          <img src="assets/images/icons/edit.svg" alt="Edit" class="editing-banner__icon">
          <span class="editing-banner__text">{{ 'translation.manager.editing_mode_active' | translateLang }}</span>
        </div>
        <div class="editing-banner__badges">
          <span class="editing-banner__badge">{{ selectedLanguage()?.name }}</span>
          <span class="editing-banner__separator">•</span>
          <span class="editing-banner__badge">{{ selectedCategory() === 'all' ? ('translation.manager.all_categories' | translateLang) : selectedCategory() }}</span>
        </div>
      </div>
      <div class="editing-banner__right">
        <span class="editing-banner__changes-text">{{ 'translation.manager.unsaved_changes' | translateLang: {count: dirtyTranslations().length} }}</span>
      </div>
    </div>
  }

  <!-- Translations Table -->
  <div class="translations-container">
    @if (isLoading()) {
      <div class="loading-state">
        <div class="spinner"></div>
        <p>{{ 'translation.manager.loading' | translateLang }}</p>
      </div>
    } @else if (filteredTranslations().length === 0) {
      <div class="empty-state">
        <i class="bi bi-translate"></i>
        <p>{{ 'translation.manager.no_results' | translateLang }}</p>
      </div>
    } @else {
      <div class="translations-table">
        <!-- Table Header -->
        <div class="table-header">
          <div class="header-cell key-column">{{ 'translation.manager.translation_key' | translateLang }}</div>
          <div class="header-cell description-column">{{ 'translation.manager.description' | translateLang }}</div>
          <div class="header-cell english-column">{{ 'translation.manager.english_translation' | translateLang }}</div>
          <div class="header-cell value-column">{{ 'translation.manager.value' | translateLang }}</div>
        </div>

        <!-- Table Body -->
        <cdk-virtual-scroll-viewport itemSize="68" class="table-body">
          <div *cdkVirtualFor="let entry of filteredTranslations(); trackBy: trackByKey"
               class="table-row">
            <div class="table-cell key-column">
              <span class="key-badge">{{ entry.key }}</span>
            </div>
            <div class="table-cell description-column">
              <span class="description-text">{{ entry.description }}</span>
            </div>
            <div class="table-cell english-column">
              <span class="english-text">{{ entry.englishTranslation }}</span>
            </div>
            <div class="table-cell value-column">
              <input
                type="text"
                class="value-input"
                [value]="entry.value"
                (input)="onTranslationChange(entry, $any($event.target).value)"
                [placeholder]="'translation.manager.enter_value' | translateLang">
            </div>
          </div>
        </cdk-virtual-scroll-viewport>
      </div>
    }
  </div><!-- /.translations-container -->
  </div><!-- /.dialog-scroll-body -->
</div>
